import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { EndpointError, ErrorCode } from './errors';
import { mergeDeep } from '@ngx-translate/core';
import { Err, Ok, Result } from '@gametailors/web-core/classes';
import { PanicError } from '@gametailors/web-core/errors/panic';
import { GeneratedPaginationParams } from '@repo/backend-api-spec';

interface BaseRequestOptions {
  params?: Record<string, string>;
  // In node, you can use the IncomingHttpHeaders type for the key, but that is not available in Angular
  headers?: Record<string, string>;
  withCredentials?: boolean;
  credentials?: 'include' | 'omit' | 'same-origin';
  withBaseUrl?: boolean;
  context?: HttpContext;
  pagination?: Partial<GeneratedPaginationParams>;
}

interface RequestOptionsBody<T> extends BaseRequestOptions {
  body: T;
}

// noinspection JSUnusedGlobalSymbols
export abstract class BaseAPI {
  protected abstract readonly BASE_URL: string;
  protected abstract readonly http: HttpClient;

  protected get<Response = object, Error extends ErrorCode = 'UNKNOWN'>(
    route: string,
    options?: BaseRequestOptions,
  ): Observable<Result<Response, EndpointError<Error>>> {
    return this.http
      .get<Response>(this.buildUrl(route, options?.withBaseUrl ?? true), {
        params: this.serializeQueryParams(mergeDeep(options?.pagination ?? {}, options?.params ?? {})),
        headers: options?.headers,
        withCredentials: options?.withCredentials ?? true,
        credentials: options?.credentials ?? 'include',
        context: options?.context,
      })
      .pipe(
        map((r) => Ok(r)),
        catchError((error: HttpErrorResponse) => of(Err(this.handleError<Error>(error.error)))),
      );
  }

  protected post<Body = object, Response = object, Error extends ErrorCode = 'UNKNOWN'>(
    route: string,
    options: RequestOptionsBody<Body>,
  ): Observable<Result<Response, EndpointError<Error>>> {
    return this.http
      .post<Response>(this.buildUrl(route, options?.withBaseUrl ?? true), options.body, {
        params: this.serializeQueryParams(mergeDeep(options.pagination ?? {}, options.params ?? {})),
        headers: options.headers,
        withCredentials: options.withCredentials ?? true,
        credentials: options.credentials ?? 'include',
        context: options.context,
      })
      .pipe(
        map((r) => Ok(r)),
        catchError((error: HttpErrorResponse) => of(Err(this.handleError<Error>(error.error)))),
      );
  }

  protected put<Body = object, Response = object, Error extends ErrorCode = 'UNKNOWN'>(
    route: string,
    options: RequestOptionsBody<Body>,
  ): Observable<Result<Response, EndpointError<Error>>> {
    return this.http
      .put<Response>(this.buildUrl(route, options?.withBaseUrl ?? true), options.body, {
        params: this.serializeQueryParams(mergeDeep(options.pagination ?? {}, options.params ?? {})),
        headers: options.headers,
        withCredentials: options.withCredentials ?? true,
        credentials: options.credentials ?? 'include',
        context: options.context,
      })
      .pipe(
        map((r) => Ok(r)),
        catchError((error: HttpErrorResponse) => of(Err(this.handleError<Error>(error.error)))),
      );
  }

  protected patch<Body = object, Response = object, Error extends ErrorCode = 'UNKNOWN'>(
    route: string,
    options: RequestOptionsBody<Body>,
  ): Observable<Result<Response, EndpointError<Error>>> {
    return this.http
      .patch<Response>(this.buildUrl(route, options?.withBaseUrl ?? true), options.body, {
        params: this.serializeQueryParams(mergeDeep(options.pagination ?? {}, options.params ?? {})),
        headers: options.headers,
        withCredentials: options.withCredentials ?? true,
        credentials: options.credentials ?? 'include',
        context: options.context,
      })
      .pipe(
        map((r) => Ok(r)),
        catchError((error: HttpErrorResponse) => of(Err(this.handleError<Error>(error.error)))),
      );
  }

  protected delete<Response = object, Error extends ErrorCode = 'UNKNOWN'>(
    route: string,
    options?: BaseRequestOptions,
  ): Observable<Result<Response, EndpointError<Error>>> {
    return this.http
      .delete<Response>(
        this.buildUrl(route, options?.withBaseUrl ?? true),
        options
          ? {
              params: this.serializeQueryParams(mergeDeep(options.pagination ?? {}, options.params ?? {})),
              headers: options.headers,
              withCredentials: options.withCredentials ?? true,
              credentials: options.credentials ?? 'include',
              context: options.context,
            }
          : {},
      )
      .pipe(
        map((r) => Ok(r)),
        catchError((error: HttpErrorResponse) => of(Err(this.handleError<Error>(error)))),
      );
  }

  private handleError<Error extends ErrorCode = 'UNKNOWN'>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any,
  ) {
    if (!error) throw new PanicError('`error` is falsely: ' + error);
    if (!('code' in error)) throw new PanicError('Failed to parse error as EndpointError: ' + JSON.stringify(error));
    return new EndpointError<Error>(error);
  }

  private serializeQueryParams(params: Record<string, string | object>) {
    return Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : value]),
    );
  }

  private buildUrl(route: string, withBaseUrl: boolean) {
    return withBaseUrl ? `${this.BASE_URL}${route.startsWith('/') ? route : `/${route}`}` : route;
  }
}
